import React, { useEffect, useRef } from 'react';
import {
  FiAlertTriangle,
  FiBookOpen,
  FiCheck,
  FiCheckCircle,
  FiChevronLeft,
  FiEdit3,
  FiRefreshCw,
  FiTarget,
} from 'react-icons/fi';
import MathMarkdown from './MathMarkdown';
import ModelAnswerPanel from './ModelAnswerPanel';

function toTextList(value) {
  return Array.isArray(value) ? value.filter((item) => typeof item === 'string' && item.trim()) : [];
}

function ResultList({ items, icon: Icon = FiCheck }) {
  const safeItems = toTextList(items);
  if (safeItems.length === 0) return null;
  return (
    <ul>
      {safeItems.map((item, index) => (
        <li key={`${item}-${index}`}><Icon aria-hidden="true" /><MathMarkdown value={item} /></li>
      ))}
    </ul>
  );
}

function EssayResult({ result, meta, subject, onEdit, onReset, historyMode = false, onHistoryBack }) {
  const headingRef = useRef(null);
  const isSolveMode = result.taskType === 'solve';
  const assessmentType = result.assessmentType === 'constructed' ? 'constructed' : 'essay';
  const assessmentName = assessmentType === 'constructed' ? '서술형' : '논술';
  const rawScore = Number(result.overall?.score);
  const rawConfidence = Number(result.detected?.confidence);
  const score = Number.isFinite(rawScore) ? Math.min(100, Math.max(0, Math.round(rawScore))) : 0;
  const confidence = Number.isFinite(rawConfidence) ? Math.min(100, Math.max(0, Math.round(rawConfidence))) : 0;
  const rubric = Array.isArray(result.rubric) ? result.rubric.filter((item) => item && typeof item === 'object') : [];
  const corrections = Array.isArray(result.corrections)
    ? result.corrections.filter((item) => (
        item
        && typeof item.original === 'string'
        && typeof item.revised === 'string'
        && item.original.trim()
        && item.revised.trim()
      ))
    : [];
  const warnings = toTextList(result.detected?.warnings);
  const strengths = toTextList(result.strengths);
  const improvements = toTextList(result.improvements);
  const nextSteps = toTextList(result.nextSteps);
  const modelAnswer = typeof result.modelAnswer === 'string' ? result.modelAnswer : '';

  useEffect(() => {
    const frame = window.requestAnimationFrame(() => headingRef.current?.focus());
    return () => window.cancelAnimationFrame(frame);
  }, []);

  return (
    <div className="ai-essay-result" aria-labelledby="ai-essay-result-title">
      <p className="ai-essay-sr-only" role="status">AI 분석이 완료되었습니다.</p>
      {meta?.historySaved === false && (
        <section className="ai-essay-result-save-warning" role="alert">
          <FiAlertTriangle aria-hidden="true" />
          <div>
            <strong>이번 결과를 첨삭 내역에 저장하지 못했습니다.</strong>
            <p>{meta.historyError || '결과는 지금 확인할 수 있지만, 화면을 나가면 다시 불러올 수 없습니다.'}</p>
          </div>
        </section>
      )}
      <section className={`ai-essay-result-summary${isSolveMode ? ' is-solve' : ''}`}>
        {isSolveMode ? (
          <span className="ai-essay-result-summary__solve-icon"><FiBookOpen aria-hidden="true" /></span>
        ) : (
          <div
            className="ai-essay-score-ring"
            style={{ '--score': `${score * 3.6}deg` }}
            role="meter"
            aria-label="종합 점수"
            aria-valuemin="0"
            aria-valuemax="100"
            aria-valuenow={score}
          >
            <strong>{score}</strong><span>/ 100</span>
          </div>
        )}
        <div>
          <h2 id="ai-essay-result-title" ref={headingRef} tabIndex={-1}>{isSolveMode ? `${subject.name} 문제 풀이` : `${subject.name} ${assessmentName} 진단 완료`}</h2>
          <MathMarkdown
            className="ai-essay-result-summary__copy"
            value={typeof result.overall?.summary === 'string' ? result.overall.summary : 'AI 분석을 완료했습니다.'}
          />
          <div className="ai-essay-result-meta">
            {!isSolveMode && <span><FiCheckCircle aria-hidden="true" />{result.overall?.level}</span>}
            {meta?.usedAttachment && <span>첨부 자료 판독 신뢰도 {confidence}%</span>}
          </div>
        </div>
      </section>

      {warnings.length > 0 && (
        <section className="ai-essay-result-warning" role="status">
          <FiAlertTriangle aria-hidden="true" />
          <div><strong>확인이 필요한 부분</strong><ResultList items={warnings} icon={FiAlertTriangle} /></div>
        </section>
      )}

      <details className="ai-essay-result-detected" open={historyMode || undefined}>
        <summary>AI가 인식한 문제와 답안 확인</summary>
        <div>
          <article>
            <h3>인식한 문제</h3>
            <MathMarkdown value={typeof result.detected?.problem === 'string' && result.detected.problem ? result.detected.problem : '텍스트로 변환된 문제가 없습니다.'} />
          </article>
          {!isSolveMode && (
            <article>
              <h3>인식한 학생 답안</h3>
              <MathMarkdown value={typeof result.detected?.answer === 'string' && result.detected.answer ? result.detected.answer : '텍스트로 변환된 답안이 없습니다.'} />
            </article>
          )}
        </div>
      </details>

      {!isSolveMode && rubric.length > 0 && (
        <section className="ai-essay-result-panel">
          <header><span><FiTarget aria-hidden="true" /></span><div><h2>{assessmentType === 'constructed' ? '채점 기준별 진단' : '논술 영역별 진단'}</h2></div></header>
          <div className="ai-essay-rubric-list">
            {rubric.map((item, index) => {
              const label = typeof item.label === 'string' && item.label ? item.label : `평가 항목 ${index + 1}`;
              const parsedMaxScore = Number(item.maxScore);
              const maxScore = Number.isFinite(parsedMaxScore) && parsedMaxScore > 0 ? parsedMaxScore : 25;
              const parsedItemScore = Number(item.score);
              const itemScore = Number.isFinite(parsedItemScore) ? Math.min(maxScore, Math.max(0, parsedItemScore)) : 0;
              return (
                <article key={typeof item.id === 'string' && item.id ? item.id : `${label}-${index}`}>
                  <div className="ai-essay-rubric-list__heading">
                    <strong>{label}</strong><span>{itemScore} / {maxScore}</span>
                  </div>
                  <div
                    className="ai-essay-rubric-list__bar"
                    role="progressbar"
                    aria-label={`${label} 점수`}
                    aria-valuemin="0"
                    aria-valuemax={maxScore}
                    aria-valuenow={itemScore}
                  ><span style={{ width: `${(itemScore / maxScore) * 100}%` }} /></div>
                  {typeof item.evidence === 'string' && item.evidence && (
                    <div className="ai-essay-rubric-list__copy"><b>답안 근거</b><MathMarkdown value={item.evidence} /></div>
                  )}
                  {typeof item.feedback === 'string' && item.feedback && (
                    <div className="ai-essay-rubric-list__copy"><b>첨삭 의견</b><MathMarkdown value={item.feedback} /></div>
                  )}
                </article>
              );
            })}
          </div>
        </section>
      )}

      {!isSolveMode && (strengths.length > 0 || improvements.length > 0) && (
        <section className="ai-essay-result-columns">
          <article className="is-strength"><h2><FiCheckCircle aria-hidden="true" />잘한 점</h2><ResultList items={strengths} /></article>
          <article className="is-improvement"><h2><FiTarget aria-hidden="true" />보완할 점</h2><ResultList items={improvements} icon={FiTarget} /></article>
        </section>
      )}

      {!isSolveMode && corrections.length > 0 && (
        <section className="ai-essay-result-panel">
          <header><span><FiEdit3 aria-hidden="true" /></span><div><h2>{assessmentType === 'constructed' ? '답안 첨삭' : '문장별 첨삭'}</h2></div></header>
          <div className="ai-essay-correction-list">
            {corrections.map((item, index) => (
              <article key={`${item.original}-${index}`}>
                <span>{typeof item.category === 'string' && item.category ? item.category : `첨삭 ${index + 1}`}</span>
                <div><small>원문</small><MathMarkdown value={item.original} /></div>
                <div className="is-revised"><small>개선문</small><MathMarkdown value={item.revised} /></div>
                {typeof item.reason === 'string' && item.reason && <MathMarkdown className="ai-essay-correction-list__reason" value={item.reason} />}
              </article>
            ))}
          </div>
        </section>
      )}

      {modelAnswer && (
        <ModelAnswerPanel
          title={isSolveMode ? 'AI 단계별 풀이' : `${assessmentName} 예시답안`}
          value={modelAnswer}
        />
      )}

      {nextSteps.length > 0 && (
        <section className="ai-essay-result-panel ai-essay-next-steps">
          <header><span><FiTarget aria-hidden="true" /></span><div><h2>다음 학습 추천</h2></div></header>
          <ResultList items={nextSteps} />
        </section>
      )}

      <p className="ai-essay-result-notice">AI 분석 결과는 학습을 돕기 위한 참고 자료입니다. 이미지·PDF가 불명확하거나 평가 기준이 다르면 결과가 달라질 수 있습니다.</p>

      <div className="ai-essay-result-actions">
        {historyMode ? (
          <button type="button" className="is-primary is-history-back" onClick={onHistoryBack}>
            <FiChevronLeft aria-hidden="true" />첨삭 내역으로
          </button>
        ) : (
          <>
            <button type="button" className="is-secondary" onClick={onEdit}><FiEdit3 aria-hidden="true" />입력 내용 수정</button>
            <button type="button" className="is-primary" onClick={onReset}><FiRefreshCw aria-hidden="true" />새로 시작</button>
          </>
        )}
      </div>

      {meta?.requestId && <small className="ai-essay-request-id">문의 코드: {meta.requestId}</small>}
    </div>
  );
}

export default EssayResult;
